import { useContext, useState, useCallback, useEffect, useRef } from 'react';

import useReader from '../../../hooks/use-reader';

import ReaderContext from '../../../store/reader-context';
import { EnhancedOrderBox } from '../../../types/logistics';
import { barcodesMatch } from '../../../utils/barcode-format';

import { ActionBar, ActionButton } from '../../Shared/ActionBar/ActionBar';
import BoxList from '../../Shared/BoxList/BoxList';
import OCRCapture from './OCRCapture';
import { OCRMatch } from '../../../types/ocr';

import useCameraPermission from '../../../hooks/use-camera-permission';
import CameraPermissionModal from '../CameraPermissionModal/CameraPermissionModal';

import { FaQrcode, FaFont, FaTimes } from 'react-icons/fa';

import './LabelReader.scss';

function LabelReader() {
    const readerCtx = useContext(ReaderContext);
    const { stopQuagga } = useReader(null);
    const { instructions, isDismissedThisSession, markDismissed } = useCameraPermission();
    const [ocrMode, setOcrMode] = useState(false);
    const [visibleMessage, setVisibleMessage] = useState<{ type: string; text: string } | null>(null);
    const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Auto-dismiss: show latest message for 3s, then fade out
    const messages = readerCtx.messages as Array<{ type: string; text: string }>;
    useEffect(() => {
        if (messages.length > 0) {
            setVisibleMessage(messages[0]);
            if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
            dismissTimerRef.current = setTimeout(() => {
                setVisibleMessage(null);
            }, 3000);
        } else {
            setVisibleMessage(null);
        }
        return () => {
            if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
        };
    }, [messages]);

    const boxesScanned = () => {
        let progress = 0;
        readerCtx.config.boxes.forEach((box) => {
            if (box.scanned) progress++;
        });
        return progress;
    };

    // Handle OCR match - update the matched box (match by barcode or box ID)
    const handleOCRMatch = useCallback((identifier: string, match: OCRMatch) => {
        const matchedBox = readerCtx.config.boxes.find((box: EnhancedOrderBox) =>
            (box.barcode ? barcodesMatch(box.barcode, identifier) : false) || String(box.id) === identifier
        );

        if (matchedBox && !matchedBox.scanned) {
            matchedBox.scanned = true;
            readerCtx.config.update(matchedBox);
            readerCtx.sounds.ok.play();
            readerCtx.updateMessages('add', {
                type: 'ok',
                text: `${identifier} recognized via OCR${match.isExactMatch ? '' : ` (${Math.round(match.confidence * 100)}% match)`}`
            });
        }

        // Exit OCR mode after successful match
        setOcrMode(false);
    }, [readerCtx]);

    // Handle OCR error
    const handleOCRError = useCallback((error: string) => {
        readerCtx.updateMessages('add', {
            type: 'error',
            text: `OCR Error: ${error}`
        });
        readerCtx.sounds.error.play();
    }, [readerCtx]);

    // Toggle OCR mode
    const toggleOCRMode = useCallback(() => {
        setOcrMode(!ocrMode);
    }, [ocrMode]);

    // Close OCR mode
    const closeOCRMode = useCallback(() => {
        setOcrMode(false);
    }, []);

    return (
        <div id="reader" className={readerCtx.started ? 'shown' : ''}>
            <div className="inner">
                <div id="quagga"></div>
                <div className="feedback">
                    {readerCtx.cameraBlocked && !isDismissedThisSession() && (
                        <div className="title">
                            <h3>Camera access is needed</h3>
                            <p>Enable camera access in your browser or device settings to scan barcodes.</p>
                        </div>
                    )}
                    {readerCtx.cameraBlocked && isDismissedThisSession() && (
                        <div className="title">
                            <h3>Camera blocked</h3>
                            <p>Camera access is blocked. Check your browser or device settings to enable it.</p>
                        </div>
                    )}
                    {!readerCtx.cameraBlocked && boxesScanned() === 0 && <div className="title">
                        <h3>Nothing scanned yet</h3>
                        <p>Please point your camera at a barcode.</p>
                    </div>}
                    {boxesScanned() > 0 && (
                        <div className="scan-progress-summary">
                            <span className="scanned-count">{boxesScanned()}</span>
                            <span className="scanned-label"> of {readerCtx.config.boxes.length} scanned</span>
                        </div>
                    )}
                    {visibleMessage && (
                        <p key={visibleMessage.text} className={'message ' + visibleMessage.type}>{visibleMessage.text}</p>
                    )}
                    {readerCtx.config.boxes.length > 0 && <BoxList boxes={readerCtx.config.boxes} progress={0} update={readerCtx.config.update} />}
                </div>
                {!ocrMode && (
                    <ActionBar className="reader-actions">
                        <ActionButton variant="primary" icon={<FaQrcode />} onClick={() => { if (ocrMode) setOcrMode(false); }}>
                            Scan Barcode
                        </ActionButton>
                        <ActionButton icon={<FaFont />} onClick={toggleOCRMode}>
                            Read Text
                        </ActionButton>
                        <ActionButton variant="secondary" icon={<FaTimes />} onClick={stopQuagga}>
                            Close
                        </ActionButton>
                    </ActionBar>
                )}

                {/* OCR Capture Overlay */}
                {ocrMode && (
                    <OCRCapture
                        boxes={readerCtx.config.boxes}
                        onMatch={handleOCRMatch}
                        onClose={closeOCRMode}
                        onError={handleOCRError}
                    />
                )}

                {/* Camera Permission Modal — shown when camera access is blocked */}
                {readerCtx.cameraBlocked && !isDismissedThisSession() && (
                    <CameraPermissionModal
                        visible={true}
                        instructions={instructions}
                        onDismiss={() => {
                            markDismissed();
                            stopQuagga();
                        }}
                        onReload={() => window.location.reload()}
                    />
                )}
            </div>
        </div>
    );
}

export default LabelReader;